/**
 * The call log's forwarding switch.
 *
 * In the rail's Settings section, next to "would the phone ring here":
 * everything in that group is state about the telephone itself, and nothing
 * here narrows the rows below it. It is also the screen an operator is already
 * looking at when they get up to leave, which is the entire reason this exists
 * rather than living only in Settings - a switch that needs three clicks
 * through a settings menu is a switch nobody throws on the way out of the door.
 *
 * The state is not fetched here. It arrives from `useCallDivert`, held by the
 * rail, because the rail draws a marker on the closed heading when calls are
 * being forwarded - and a section that can be shut must not be the only thing
 * that knows where the telephone is pointed.
 *
 * Two rows, and the split is deliberate. The first one IS the switch: with a
 * number saved, one press sends every incoming call to it and one press brings
 * them back. The second opens the panel, which is for the parts that are worth
 * a second of thought - which number, and for how long.
 *
 * What the row draws is `active`, not `enabled`. The two come apart on an expiry
 * that has passed, a cleared number, a line that is not answered at all, and the
 * automatic night rule firing on its own - and "where do my calls go right now"
 * is the only question worth answering here.
 */
import { PhoneForwarded, Settings2 } from "lucide-react";
import { cn, DateTimePicker } from "@wa/ui";
import { useI18n } from "../../../i18n/I18nProvider";
import { formatPhone } from "../../../lib/phone";
import { Pop, useClosePops } from "../../shell/Pop";
import type { CallDivertApi } from "./useCallDivert";

/** The expiry as a clock, in the reader's own locale. */
function atTime(iso: string): string {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? "" : at.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export function CallDivert({ divert }: { divert: CallDivertApi }) {
  // The locale as well as the translator: the date picker writes its own label
  // and needs to know which week starts on Monday.
  const { t, locale } = useI18n();
  const close = useClosePops();
  const { state, busy, to, setTo, until, setUntil, write } = divert;

  // Nothing to say until the line answers its calls at all. An installation
  // that refuses every incoming call has nothing to report about where they
  // would be sent - the same rule the reach indicator beside it follows.
  if (!state?.inboundReady) return null;

  const active = state.active;
  // Both standing rules. Neither is switchable from here - they are rules, not
  // moments - so while one of them is doing the forwarding the row reports and
  // does not offer to turn anything off.
  const automatic = active && (state.reason === "after-hours" || state.reason === "schedule");
  const label = active
    ? formatPhone(state.to)
    : state.to
      ? t("calls.forward.off")
      : t("calls.forward.unset");

  return (
    <>
      <div className="crm-msect">{t("calls.forward.title")}</div>

      {/* One press. The automatic night rule is not switchable from here - it is
          a rule, not a moment - so while it is doing the diverting the row
          reports and does not offer to turn anything off. */}
      <button
        type="button"
        className={cn("crm-mtag", active && "is-on")}
        disabled={busy || automatic || !state.to}
        aria-pressed={active}
        title={
          automatic
            ? t(
                state.reason === "schedule" ? "calls.forward.scheduleHint" : "calls.forward.autoHint",
                { phone: formatPhone(state.to) },
              )
            : active
              ? state.until
                ? t("calls.forward.onUntilHint", { time: atTime(state.until) })
                : t("calls.forward.onHint")
              : state.to
                ? t("calls.forward.offHint")
                : // Nothing to switch on yet, so the row is a statement rather
                  // than a control - it says where the switch is instead.
                  t("calls.forward.unsetHint")
        }
        onClick={() => void write(!state.enabled, to, until)}
      >
        <i
          style={{
            background: automatic
              ? "var(--color-warning)"
              : active
                ? "var(--color-success)"
                : "var(--color-muted-foreground)",
          }}
        />
        <span className="crm-mtag-l">{label}</span>
      </button>

      <Pop
        matchWidth
        trigger={(open) => (
          <button type="button" className={cn("crm-mtag", open && "is-on")} aria-expanded={open}>
            <Settings2 className="size-3.5 shrink-0" strokeWidth={1.8} />
            <span className="crm-mtag-l">{t("calls.forward.edit")}</span>
          </button>
        )}
      >
        <div className="crm-menu-head">
          <div className="crm-menu-t">{t("calls.forward.title")}</div>
          <div className="crm-menu-s">{t("calls.forward.sub")}</div>
        </div>

        <label className="crm-msearch">
          <PhoneForwarded className="size-4 shrink-0 text-muted-foreground" />
          <input
            value={to}
            onChange={(e) => setTo(e.target.value)}
            // The panel's own arrow keys walk between rows, which inside a text
            // field is a caret moving - the same stop `ClientLinkButton` makes.
            onKeyDown={(e) => {
              if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) e.stopPropagation();
            }}
            placeholder="+372..."
            inputMode="tel"
            autoComplete="off"
            aria-label={t("calls.forward.number")}
          />
        </label>

        <div className="crm-msect">{t("calls.forward.forHowLong")}</div>
        {/* The panel's own keyboard handling walks between rows and closes on
            Escape, both of which a calendar needs for itself. Stopped on the
            way out of this box rather than taught to the panel: the same
            containment the number field above makes for its caret keys. */}
        <div
          className="px-1.5 pb-1.5"
          onKeyDown={(e) => e.stopPropagation()}
        >
          <DateTimePicker
            value={until}
            locale={locale}
            placeholder={t("calls.forward.untilOff")}
            onChange={(next) => {
              setUntil(next);
              // Already forwarding: the moment is the whole edit, so it is
              // written now. There is no Save button in a rail menu, and an
              // expiry left sitting on screen is exactly the promise this
              // control exists to keep.
              if (state.enabled) void write(true, to, next);
            }}
          />
        </div>

        <div className="crm-menu-sep" />
        <button
          type="button"
          className="crm-mi"
          disabled={busy}
          onClick={() => {
            close();
            void write(!state.enabled, to, until);
          }}
        >
          <PhoneForwarded className="size-4" />
          {state.enabled ? t("calls.forward.turnOff") : t("calls.forward.turnOn")}
        </button>
      </Pop>
    </>
  );
}
